import { Item, Root, Trigger } from '@radix-ui/react-accordion';
import { FC, useId, useState } from 'react';

import Badge from 'molecules/Badge';
import Button, { ButtonProps } from 'molecules/Button';
import CollapsiblePanel from 'molecules/CollapsiblePanel';
import Icon from 'molecules/Icon';

import TableCell from 'components/Tables/PricingV2/components/TableCell';
import { PricingTableConfigProps } from 'components/Tables/PricingV2/config';
import { useScrollToAccordionItem } from 'components/Tables/PricingV2/hooks/useScrollToAccordionItem';
import { contentRowStyle, pricingPlanTitleStyle } from 'components/Tables/PricingV2/styles';

import { hasArrayValues } from 'utils/array/arrays';

interface AccordionCardHeadingProps {
  label: string;
  isHighlighted?: boolean;
  description?: string;
}

interface TableAccordionProps extends Pick<PricingTableConfigProps, 'tableSections'>, AccordionCardHeadingProps {
  node: 'tierOne' | 'tierTwo' | 'tierThree';
  link?: string;
  buttonLabel?: string;
  startIcon?: ButtonProps['startIcon'];
  endIcon?: ButtonProps['endIcon'];
  chiliPiperUrl?: string;
}

const openChiliPiperPopup = (url: string) => {
  const width = 600;
  const height = 700;
  const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
  const top = Math.round(window.screenY + (window.outerHeight - height) / 2);
  window.open(url, 'chilipiper', `width=${width},height=${height},left=${left},top=${top}`);
};

const AccordionCardHeading: FC<AccordionCardHeadingProps> = ({ isHighlighted, label, description }) => (
  <div className="flex flex-col gap-4">
    <div className="flex w-full flex-row items-center gap-4">
      <span className={pricingPlanTitleStyle({ isHighlighted: !!isHighlighted })}>{label}</span>
      {isHighlighted && <Badge label="Most Popular" color="teal" />}
    </div>
    {description && <span className="flex text-left text-md text-gray-300 lg:hidden">{description}</span>}
  </div>
);

const AccordionCard: FC<TableAccordionProps> = ({
  tableSections,
  isHighlighted,
  label,
  link,
  buttonLabel,
  startIcon,
  endIcon,
  node,
  description,
  chiliPiperUrl,
}) => {
  const [openValue, setOpenValue] = useState<string>('');
  const idPrefix = useId();
  const refs = useScrollToAccordionItem(openValue);

  return (
    <div className="flex w-full flex-col gap-4 rounded-xl border border-primary-300/30 px-4 py-6">
      <AccordionCardHeading label={label} isHighlighted={isHighlighted} description={description} />
      <Root type="single" collapsible value={openValue} onValueChange={setOpenValue} className="flex flex-col gap-4">
        {hasArrayValues(tableSections) &&
          tableSections.map(section => (
            <Item
              key={section.id}
              value={section.id}
              className="rounded-lg border border-solid border-transparent border-b-white/20 bg-white/10 data-[state=open]:border-primary-300/30"
            >
              <div
                ref={el => {
                  refs.current[section.id] = el;
                }}
              />
              <Trigger
                id={`${idPrefix}-trigger-${section.id}`}
                aria-controls={`${idPrefix}-panel-${section.id}`}
                className="flex w-full justify-between px-3 py-4 text-primary-400"
              >
                <span className="text-left text-lg font-bold text-white">{section.label}</span>
                <Icon icon="chevron-down" />
              </Trigger>
              <CollapsiblePanel
                id={`${idPrefix}-panel-${section.id}`}
                labelledBy={`${idPrefix}-trigger-${section.id}`}
                isOpen={openValue === section.id}
              >
                {hasArrayValues(section.rows) &&
                  section.rows.map(row => (
                    <div key={row.rowHeading} className={contentRowStyle({ isText: row[node]?.cell === 'text' })}>
                      <div className="w-full">
                        <div className="line-clamp-3 text-left text-md text-white">{row.rowHeading}</div>
                      </div>
                      {row[node] && <TableCell iconSize={24} {...row[node]} />}
                    </div>
                  ))}
              </CollapsiblePanel>
            </Item>
          ))}
      </Root>
      <Button
        label={buttonLabel || 'Get Started'}
        hierarchy="primary"
        startIcon={startIcon}
        endIcon={endIcon}
        className="w-full md:w-full"
        href={chiliPiperUrl ? undefined : link}
        onClick={chiliPiperUrl ? () => openChiliPiperPopup(chiliPiperUrl) : undefined}
      />
    </div>
  );
};

export default AccordionCard;
